<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { useOffcanvas } from "@/composables/useOffcanvas";
import { useCharacterArchetype } from "@/composables/useCharacterArchetype";
import type { SwadeCharacterInterface } from "@/types/characters";

const props = defineProps<{
    character: SwadeCharacterInterface | null;
}>();

const ux = useUxData();
const setupStore = useGuidedSetupStore();

const { archetype, archetypeLabel } = useCharacterArchetype(() => props.character);

const title = computed(() => ux.labels?.guidedSetup?.character?.concept ?? 'Character Concept');
const backgroundLabel = computed(() => ux.labels?.swade?.data?.characters?.summary ?? 'Background/Summary');

// The archetype and background are typed on the first step, so the panel is offered only after it.
const offered = computed(() => setupStore.currentStep > 0);

const panelRef = ref<HTMLElement | null>(null);
const open = ref(false);

// Esc closes the panel too, and only Bootstrap sees that.
const { show, hide } = useOffcanvas(panelRef, { onClose: () => { open.value = false; } });

const toggle = () => {
    if (open.value) {
        hide();
        open.value = false;
    } else {
        show();
        open.value = true;
    }
};

watch(offered, (isOffered) => {
    if (!isOffered && open.value) toggle();
});
</script>

<template>
    <Teleport v-if="archetype || character?.summary" to="#guided-setup-modal-footer">
        <button v-if="offered"
                type="button"
                class="character-concept-toggle btn btn-primary rounded-circle"
                :class="{ open }"
                :aria-label="title"
                aria-controls="character-concept-panel"
                :aria-expanded="open"
                @click="toggle"
        >
            <span class="fa-light fa-fw" :class="open ? 'fa-times' : 'fa-thought-bubble'"></span>
        </button>
    </Teleport>

    <div id="character-concept-panel"
         ref="panelRef"
         class="character-concept-panel offcanvas offcanvas-end"
         tabindex="-1"
         data-bs-backdrop="false"
         data-bs-scroll="true"
         aria-labelledby="character-concept-title"
    >
        <div class="offcanvas-header">
            <h5 id="character-concept-title" class="offcanvas-title">{{ title }}</h5>
        </div>
        <div class="offcanvas-body">
            <h6>{{ archetypeLabel }}</h6>
            <p>{{ archetype || '—' }}</p>
            <h6>{{ backgroundLabel }}</h6>
            <p class="background">{{ character?.summary || '—' }}</p>
        </div>
    </div>
</template>
